import React from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { ROUTES } from '@constants/routes';
import { scrollToTop } from '@utils/usefulFuncs';
import { AI_TOOLS, DOCUMENT_TEMPLATE_MODE_IDS } from '@constants';
import { TemplateBuilderTool } from '@containers/DigitalAnalyst/TemplateBuilderTool';
import { Page, Container, H5, Body5 } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { useDispatch } from 'react-redux';
import { uploadFilesActions } from '@constants/actionTypes';
import { DragDrop } from '@ui/DragAndDropUploader';
import { ProgressOverlay } from '@ui/ProgressOverlay';
import { UploadFilesList } from '@containers/DigitalAnalyst/UploadFilesList';
import { EditTemplateTool } from '@containers/DigitalAnalyst/EditTemplateTool';
import { addFileDefaults } from '@utils/doc';
import { DocumentUploadStatusFlatDDQTemplateGeneration } from '@/features/document/upload/DocumentUploadStatus';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 480px;
`;

const OptionCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 5px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 130px;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    background-color: ${({ theme }) => theme.themeColors.hoverBackground || 'rgba(0, 0, 0, 0.02)'};
  }

  h5 {
    font-size: 18px;
    line-height: 1.2;
    margin-bottom: 0px;
  }
`;

const modeId = DOCUMENT_TEMPLATE_MODE_IDS.portfolio_monitoring_template;
const tool = AI_TOOLS.PORTFOLIO_MONITORING_TEMPLATE;

const OPTIONS = {
  NONE: 'none',
  UPLOAD: 'upload',
  GENERATE: 'generate',
  EDIT: 'edit',
};

const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 300px;
    width: 100%;
    padding: 16px;
  }
`;

function UploadingOverlay() {
  return <ProgressOverlay message="Uploading..." />;
}

const XLSUploader = ({ onGoBack, pageWidth, reupload }) => {
  const dispatch = useDispatch();

  const [isUploading, setIsUploading] = React.useState(false);

  const handleAddFiles = (fileOrFiles) => {
    const extraParams = {
      generate_template_spec: true,
    };
    dispatch({
      type: uploadFilesActions.addFiles,
      payload: addFileDefaults(fileOrFiles, extraParams),
    });
  };

  return (
    <Page width={pageWidth}>
      {isUploading && <UploadingOverlay />}
      <BackArrowButton onClick={onGoBack} />
      <Container>
        <FormWrapper>
          <Container top={40}>
            <h3>Upload Files</h3>
            <Container top={8}>
              <Body5>
                Drag and drop a sample Excel file with historical quarterly data. This will be used to create an
                editable template with a new column added for the next quarter.
              </Body5>
            </Container>
          </Container>
          <Container top={32}>
            <DragDrop onAddFiles={handleAddFiles} xls />
          </Container>
          <Container top={16}>
            <UploadFilesList reupload={reupload} isUploading={isUploading} setIsUploading={setIsUploading} />
          </Container>
          <Container top={24}>
            <DocumentUploadStatusFlatDDQTemplateGeneration hideIfNoDocs />
          </Container>
        </FormWrapper>
      </Container>
    </Page>
  );
};

function PortfolioMonitoringPage() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = React.useState(OPTIONS.NONE);

  const onGoBack = () => {
    if (selectedOption !== OPTIONS.NONE) {
      setSelectedOption(OPTIONS.NONE);
    } else {
      navigate(ROUTES.home);
    }
  };

  React.useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        {selectedOption === OPTIONS.NONE ? (
          <Page width="1100px">
            <Container top={24} bottom={24}>
              <BackArrowButton onClick={onGoBack} />
            </Container>
            <Container>
              <H5>Portfolio Monitoring</H5>
            </Container>
            <Container top={24}>
              <Body5 bold>Select an option</Body5>
            </Container>
            <Container top={32}>
              <OptionsWrapper>
                <OptionCard onClick={() => setSelectedOption(OPTIONS.UPLOAD)}>
                  <H5>Generate Template</H5>
                  <Body5>
                    Upload a sample Excel file with historical quarterly data to create an editable template with a new
                    column added for the next quarter
                  </Body5>
                </OptionCard>
                <OptionCard onClick={() => setSelectedOption(OPTIONS.GENERATE)}>
                  <H5>Create Report</H5>
                  <Body5>Create a portfolio monitoring report from selected documents</Body5>
                </OptionCard>
                <OptionCard onClick={() => setSelectedOption(OPTIONS.EDIT)}>
                  <H5>Edit Template</H5>
                  <Body5>Edit an uploaded portfolio monitoring template</Body5>
                </OptionCard>
              </OptionsWrapper>
            </Container>
          </Page>
        ) : selectedOption === OPTIONS.EDIT ? (
          <Page width={1400}>
            <EditTemplateTool onGoBack={onGoBack} modeId={modeId} tool={tool} />
          </Page>
        ) : selectedOption === OPTIONS.UPLOAD ? (
          <>
            <XLSUploader pageWidth={1400} onGoBack={() => setSelectedOption(OPTIONS.NONE)} />
          </>
        ) : (
          <TemplateBuilderTool onGoBack={onGoBack} modeId={modeId} tool={tool} />
        )}
      </Body>
    </PageWrapper>
  );
}

export default PortfolioMonitoringPage;
